import { Component, OnInit, OnDestroy, TemplateRef, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiService } from '../../services/api.service';
import { UserService } from '../../services/user.service';
import {
  FormBuilder,
  FormControl,
  FormArray,
  FormGroup,
  Validators,
  AbstractControl,
  NgForm,
} from '@angular/forms';
import { register as registerSwiperElements } from 'swiper/element/bundle';
import { NotificationService } from '../../services/notification.service';
import { firstValueFrom, Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { LoadingService } from '../../services/loading.service';
import { EnquireService } from '../../services/enquire.service';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { ConfirmDialogComponent } from '../shared/confirm-dialog/confirm-dialog.component';

registerSwiperElements();

@Component({
  selector: 'app-new-enquiry',
  templateUrl: './new-enquiry.component.html',
  styleUrls: ['./new-enquiry.component.scss'],
})
export class NewEnquiryComponent implements OnInit, OnDestroy {
  vehicleAttachments: any; 
  uploading: boolean = false;
  isLoading = false;
  locationErr: boolean = false;
  searching = false;
  selectedLocation: string;
  selectedVehicleAttachmentType: string;
  vehDetails: any;
  vehicleAttachmentList: any[] = [];
  partErrors: any[] = [];
  errorMessages: any[] = [];
  locations: any[] = [];
  showAttachmentModal: boolean = false;
  selectedAttachments: any[] = [];
  isBulkupload: boolean = true;
  isModalSubmitted = true; // Changed to true by default

  templateDownloaded = false;
  excelDataLoaded = false;
  excelPreview: any[] = [];

  excelModalForm: FormGroup;
  excelModalFormArray!: FormArray;

  showExcelModal = false;
  selectedExcelFile: File | null = null;
  excelDataFetched: boolean = false;

  makeList: any[] = [];
  modelList: any[] = [];
  transmissionTypeList: any[] = [];
  regYearList: any[] = [];
  variantList: any[] = [];
  fuelTypeList: any[] = [];

  // Step-by-step flow properties
  currentVehicleStep: string = 'make';
  isEditingVehicle: boolean = false;
  showAdditionalDetails: boolean = false;
  voiceDraftId: number | null = null;
  originalVoiceVehicle: any = null;

  noteId: string | null = null;
  behalfNoteDetails: any = null;
  isLoadingBehalfNote: boolean = false;
  isBehalfNoteCollapsed: boolean = false;

  selectedImage: string | null = null;
  isPlayingAudio: boolean = false;
  audioElement: HTMLAudioElement | null = null;

  toggleBehalfNoteCollapse() {
    this.isBehalfNoteCollapsed = !this.isBehalfNoteCollapsed;
  }

  openImageModal(imgUrl: string) {
    this.selectedImage = imgUrl;
  }

  closeImageModal() {
    this.selectedImage = null;
  }

  playAudio(url: string) {
    if (this.audioElement && this.isPlayingAudio) {
      this.audioElement.pause();
      this.isPlayingAudio = false;
      return;
    }

    if (!this.audioElement) {
      this.audioElement = new Audio(url);
      this.audioElement.onended = () => {
        this.isPlayingAudio = false;
      };
    }
    
    this.audioElement.play().catch(e => {
      console.error('Error playing audio', e);
      this.notify.showError('Could not play audio', '');
    });
    this.isPlayingAudio = true;
  }

  toggleAdditionalDetails() {
    this.showAdditionalDetails = !this.showAdditionalDetails;
    if (!this.showAdditionalDetails) {
      this.registerForm.patchValue({
        vehNo: '',
        chassisNo: '',
        color: ''
      });
    }
  }
  selectedVehicle: any = {
    make: '',
    makeId: null,
    model: '',
    modelId: null,
    fuel: '',
    fuelId: null,
    transmission: '',
    transmissionId: null,
    year: '',
    yearId: null,
    variant: '',
    variantId: null
  };

  // Add autocomplete properties
  partDescriptionSuggestions: any[] = [];

  partNoSuggestions: any[] = [];
  activePartNoIndex: number | null = null;
  partDescReadonly: boolean[] = [];
  private partNoSearchSubject = new Subject<{ query: string, index: number }>();
  showSuggestions: { [key: number]: boolean } = {};
  searchTimeout: any;

  @ViewChild('excelPreviewModal') excelPreviewModal: TemplateRef<any>;

  // Vehicle document types
  vehicleDocList = [
    { id: 1, value: 'Vehicle Rc Copy' },
    { id: 2, value: 'Vehicle Front Photo' },
    { id: 3, value: 'Vehicle Rear Photo' },
    { id: 4, value: 'Vehicle Side Photo' },
    { id: 5, value: 'Parts Photo' },
  ];

  registerForm: FormGroup;

  constructor(
    private formBuilder: FormBuilder,
    private user: UserService,
    private route: ActivatedRoute,
    public router: Router,
    private http: ApiService,
    private notify: NotificationService,
    private loadingService: LoadingService,
    private enquireService: EnquireService,
    private modalService: NgbModal
  ) {}

  ngOnInit(): void {
    // Initialize form directly
    this.initializeForm();
    
    // Load required data
    this.locationList();
    this.loadMakeList();

    this.excelModalForm = this.formBuilder.group({
      parts: this.excelModalFormArray,
    });

    this.route.queryParams.subscribe((params) => {
      if (params['enquiryType']) {
        this.registerForm.patchValue({ enquiryType: params['enquiryType'] });
      } else {
        this.registerForm.patchValue({ enquiryType: '2' });
      }
      
      if (params['note_id']) {
        this.noteId = params['note_id'];
        this.fetchBehalfNoteDetails();
      }
    });

    this.partNoSearchSubject.pipe(debounceTime(400)).subscribe(({ query, index }) => {
      this.http.get(`retailers/getPartCode?q=${query}`).subscribe((res: any) => {
        if (res.code === 1 && res.data && res.data.length > 0) {
          this.partNoSuggestions = res.data;
        } else {
          this.partNoSuggestions = [];
        }
      });
    });
  }

  showVehicleStepper: boolean = false;

  getVehicleDetails() {
    const regNo = this.registerForm.get('vehNo')?.value;
    if (!regNo) return;
    
    this.isLoading = true;
    this.http.post('whatsapp/getVehicleDetailsByRegistrationNumber', { vehicleNo: regNo }).subscribe({
      next: (res: any) => {
        this.isLoading = false;
        const apiResponse = res.response || res;
        
        if (apiResponse.code === 1 && apiResponse.data) {
          const v = apiResponse.data;
          
          const isTransmissionRequired = v.transmission_selection_required === 1 || !v.transmission_id;

          this.registerForm.patchValue({
            make: v.make_id || v.make || '',
            model: v.model_id || v.model || '',
            fuelType: v.fuel_type_id || v.fuel_type || '',
            transmissionType: isTransmissionRequired ? '' : (v.transmission_id || v.transmission || ''),
            variant: isTransmissionRequired ? '' : (v.variant_id || v.variant || ''),
            regDate: isTransmissionRequired ? '' : (v.year_id || v.year || v.regDate || ''),
            color: v.color || '',
            chassisNo: v.chassisNo || '',
            noCylinders: v.noCylinders || '',
            cc: v.cc || ''
          });

          this.selectedVehicle = {
             make: v.make || '',
             makeId: v.make_id || null,
             model: v.model || '',
             modelId: v.model_id || null,
             fuel: v.fuel_type || '',
             fuelId: v.fuel_type_id || null,
             transmission: isTransmissionRequired ? '' : (v.transmission || ''),
             transmissionId: isTransmissionRequired ? null : (v.transmission_id || null),
             year: isTransmissionRequired ? '' : (v.year || ''),
             yearId: isTransmissionRequired ? null : (v.year_id || null),
             variant: isTransmissionRequired ? '' : (v.variant || ''),
             variantId: isTransmissionRequired ? null : (v.variant_id || null)
          };
          
          if (isTransmissionRequired) {
            this.showVehicleStepper = true;
            this.currentVehicleStep = 'transmission';
            if (this.selectedVehicle.fuelId) {
              this.loadTransmissionTypeList(this.selectedVehicle.fuelId);
            }
            this.notify.showSuccess('Vehicle partially matched, please select transmission.', '');
          } else {
            this.showVehicleStepper = false;
            this.isEditingVehicle = false;
            this.notify.showSuccess('Vehicle details fetched successfully', '');
          }
        } else {
          this.notify.showError(apiResponse.message || 'Failed to fetch vehicle details', '');
        }
      },
      error: () => {
        this.isLoading = false;
        this.notify.showError('Failed to fetch vehicle details', '');
      }
    });
  }

  fetchBehalfNoteDetails() {
    if (!this.noteId) return;
    
    this.isLoadingBehalfNote = true;
    this.http.get(`retailers/getEnquiryNoteDetails?note_id=${this.noteId}`).subscribe({
      next: (res: any) => {
        this.isLoadingBehalfNote = false;
        if (res.code === 1) {
          this.behalfNoteDetails = res.data;
        } else {
          this.notify.showError(res.message, '');
        }
      },
      error: () => {
        this.isLoadingBehalfNote = false;
        this.notify.showError('Failed to fetch note details', '');
      }
    });
  }

  ngOnDestroy() {
    if (this.audioElement) {
      this.audioElement.pause();
      this.audioElement = null;
    }
  }

  // Form getters
  get partDetails() {
    return this.registerForm.get('partDetails') as FormArray;
  }

  get vehicleAttachmentDetails() {
    return this.registerForm.get('vehicleAttachmentDetails') as FormArray;
  }

  initializeForm() {
    this.registerForm = this.formBuilder.group({
      enquiryType: ['2'],
      vehNo: [''],
      chassisNo: [''],
      make: ['', Validators.required],
      model: ['', Validators.required],
      variant: ['', Validators.required],
      regDate: ['', Validators.required],
      fuelType: ['', Validators.required],
      transmissionType: ['', Validators.required],
      color: [''],
      noCylinders: [''],
      cc: [''],
      location: ['', Validators.required],
      partType: ['1'],
      partDetails: this.formBuilder.array([this.createPartFormGroup()]),
      vehicleAttachmentDetails: this.formBuilder.array([
        this.createVehAttachmentFormGroup(),
      ]),
      isBulkupload: [false],
    });
  }

  // Load methods
  loadMakeList() {
    this.http.get('retailers/getMakeList').subscribe({
      next: (res: any) => {
        if (res.code === 1) {
          this.makeList = res.data || [];

        if (this.makeList.length === 1) {
          const make = this.makeList[0];
          this.registerForm.patchValue({
            make: this.makeList[0].id
          });
     
          this.selectedVehicle.make = make.title;
          this.selectedVehicle.makeId = make.id;

          this.onMakeChange(make.id);
        }
        } else {
          this.makeList = [];
          console.error('Error loading make list:', res.message);
        }
      },
      error: (error) => {
        console.error('Error loading make list:', error);
        this.makeList = [];
        this.notify.showError('Failed to load make list', '');
      },
    });
  }

  onMakeChange(event: any) {
    const makeValue = event?.target?.value ?? event;

    // Reset model and variant selection when make changes
    this.registerForm.get('model')?.setValue('');
    this.registerForm.get('fuelType')?.setValue('');
    this.registerForm.get('transmissionType')?.setValue('');
    this.registerForm.get('regDate')?.setValue('');
    this.registerForm.get('variant')?.setValue('');
    this.modelList = [];
    this.fuelTypeList = [];
    this.transmissionTypeList = [];
    this.regYearList = [];
    this.variantList = [];

    if (makeValue) {
      // Find the selected make object to get its ID
      const selectedMake = this.makeList.find(make => make.id == makeValue);
      if (selectedMake && selectedMake.id) {
        this.currentVehicleStep = 'model';
        this.loadModelList(selectedMake.id);
      }
    }
  }

  loadModelList(makeId: number) {
    if (!makeId) {
      this.modelList = [];
      return;
    }

    this.http
      .get('retailers/getModelList', {
        params: { makeId: makeId.toString() },
      })
      .subscribe({
        next: (res: any) => {
          if (res.code === 1) {
            this.modelList = res.data || res.data.records || [];

            if (this.modelList.length === 1) {
              const model = this.modelList[0];
              this.registerForm.patchValue({
                model: this.modelList[0].id
              });

                this.selectedVehicle.model = model.name;
                this.selectedVehicle.modelId = model.id;

              this.onModelChange(model.id);
            }
          } else {
            this.modelList = [];
            console.error('Error loading model list:', res.message);
          }
        },
        error: (error) => {
          console.error('Error loading model list:', error);
          this.modelList = [];
          this.notify.showError('Failed to load model list', '');
        },
      });
  }

  onModelChange(event: any) {
    const modelValue = event?.target?.value ?? event;

    this.registerForm.get('fuelType')?.setValue('');
    this.registerForm.get('transmissionType')?.setValue('');
    this.registerForm.get('regDate')?.setValue('');
    this.registerForm.get('variant')?.setValue('');
    this.fuelTypeList = [];
    this.transmissionTypeList = [];
    this.regYearList = [];
    this.variantList = [];

    if (modelValue) {
      // Find the selected make and model objects to get their IDs
      const makeValue = this.registerForm.get('make')?.value;
      const selectedMake = this.makeList.find(make => make.id == makeValue);
      const selectedModel = this.modelList.find(model => model.id == modelValue);
      
      if (selectedMake && selectedModel && selectedMake.id && selectedModel.id) {
        this.currentVehicleStep = 'fuel';
        this.loadFuelTypeList(selectedModel.id);
      }
    }
  }

  loadFuelTypeList(modelId: number) {
    if (!modelId) {
      this.fuelTypeList = [];
      return;
    }
    this.http
      .get('retailers/getFuelTypeLists', { params: { modelId: modelId.toString() } })
      .subscribe({
        next: (res: any) => {
          if (res.code === 1) {
            this.fuelTypeList = res.data || [];

            if (this.fuelTypeList.length === 1) {
              const fuelType = this.fuelTypeList[0];
              this.registerForm.patchValue({
                fuelType: this.fuelTypeList[0].id
              });

              this.selectedVehicle.fuel = fuelType.name;
              this.selectedVehicle.fuelId = fuelType.id;
              
              this.onFuelTypeChange(fuelType.id);
            }
          } else {
            this.fuelTypeList = [];
            console.error('Error loading fuel type list:', res.message);
          }
        },
        error: (error) => {
          console.error('Error loading fuel type list:', error);
          this.fuelTypeList = [];
          this.notify.showError('Failed to load fuel type list', '');
        },
      });
  }

  onFuelTypeChange(event: any) {
    const fuelTypeValue = event?.target?.value ?? event;
    this.registerForm.get('transmissionType')?.setValue('');
    this.transmissionTypeList = [];
    if (fuelTypeValue) {
      this.currentVehicleStep = 'transmission';
      this.loadTransmissionTypeList(fuelTypeValue);
    }
  }

  loadTransmissionTypeList(fuelTypeId: number) {
    if (!fuelTypeId) {
      this.transmissionTypeList = [];
      return;
    }
    this.http
      .get('retailers/getTransmissionLists', { params: { fuelMappingId: fuelTypeId.toString() } })  
      .subscribe({
        next: (res: any) => {
          if (res.code === 1) {
            this.transmissionTypeList = res.data || [];

            if (this.transmissionTypeList.length === 1) {
              const transmission = this.transmissionTypeList[0];
              this.registerForm.patchValue({
                transmissionType: this.transmissionTypeList[0].id
              });
              this.selectedVehicle.transmission = transmission.name;
              this.selectedVehicle.transmissionId = transmission.id;

              this.onTransmissionTypeChange(this.transmissionTypeList[0].id);
            }
          } else {
            this.transmissionTypeList = [];
            console.error('Error loading transmission type list:', res.message);
          }
        },
        error: (error) => {
          console.error('Error loading transmission type list:', error);
          this.transmissionTypeList = [];
          this.notify.showError('Failed to load transmission type list', '');
        },
      });
  }

  onTransmissionTypeChange(event: any) {
    const transmissionTypeValue = event?.target?.value ?? event;
    this.registerForm.get('regDate')?.setValue('');
    this.regYearList = [];
    if (transmissionTypeValue) {
       this.currentVehicleStep = 'year';
      this.loadRegYearList(transmissionTypeValue);
    }
  }

  loadRegYearList(transmissionTypeId: number) {
    if (!transmissionTypeId) {
      this.regYearList = [];
      return;
    }
    this.http
      .get('retailers/getYearLists', { params: { transmissionId: transmissionTypeId.toString() } })
      .subscribe({
        next: (res: any) => {
          if (res.code === 1) {
            this.regYearList = res.data || [];

            if (this.regYearList.length === 1) {
              const regYear = this.regYearList[0];
              this.registerForm.patchValue({
                regDate: this.regYearList[0].id
              });
              this.selectedVehicle.year = regYear.year;
              this.selectedVehicle.yearId = regYear.id;
              this.onRegDateChange(this.regYearList[0].id);
            }
          } else {
            this.regYearList = [];
            console.error('Error loading registration year list:', res.message);
          }
        },
        error: (error) => {
          console.error('Error loading registration year list:', error);
          this.regYearList = [];
          this.notify.showError('Failed to load registration year list', '');
        },
      });
  }

  onRegDateChange(event: any) {
    const regDateValue = event?.target?.value ?? event;
    this.registerForm.get('variant')?.setValue('');
    this.variantList = [];
    if (regDateValue) {
      const transmissionValue = this.registerForm.get('transmissionType')?.value;
      const reg = this.registerForm.get('regDate')?.value;
      if (transmissionValue && reg) {
        this.currentVehicleStep = 'variant';
        this.loadVariantList(transmissionValue, reg);
      }
    }
  }

  loadVariantList(transmissionId: number, yearId: number) {
    if (!transmissionId || !yearId) {
      this.variantList = [];
      return;
    }

    this.http
      .get('retailers/getVariantList', {
        params: { transmissionId: transmissionId.toString(), yearId: yearId.toString() },
      })
      .subscribe({
        next: (res: any) => {
          if (res.code === 1) {
            this.variantList = res.data || res.data.records || [];

            if (this.variantList.length === 1) {
              const variant = this.variantList[0];
              this.registerForm.patchValue({
                variant: this.variantList[0].id
              });

              this.selectedVehicle.variant = variant.name;
              this.selectedVehicle.variantId = variant.id;

              this.isEditingVehicle = false;
            }
          } else {
            this.variantList = [];
            console.error('Error loading variant list:', res.message);
          }
        },
        error: (error) => {
          console.error('Error loading variant list:', error);
          this.variantList = [];
          this.notify.showError('Failed to load variant list', '');
        },
      });
  }

  selectBrandStep(brand: any) {
    let selectedBrand = brand;

    if (!brand.id && this.makeList.length > 0) {
      const match = this.makeList.find(b => b.name.toUpperCase() === brand.name.toUpperCase());
      if (match) {
        selectedBrand = match;
      }
    }

    this.resetVehicleFieldsFrom('model');

    // Only support Maruti (id=9)
    // if (selectedBrand.id !== 9) {

    //   this.notify.showWarning(`We are currently not serving this brand. Support for ${selectedBrand.title || selectedBrand.name} will be available shortly.`, '');
    //   // Clear selection if not Maruti
    //   this.selectedVehicle.make = '';
    //   this.selectedVehicle.makeId = null;
    //   this.registerForm.get('make')?.setValue(null);
    //   return;
    // }

    // Reset all subsequent fields when make changes


    this.selectedVehicle.make = selectedBrand.title;
    this.selectedVehicle.makeId = selectedBrand.id;

    this.registerForm.get('make')?.setValue(selectedBrand.id);
    this.loadModelList(selectedBrand.id);
    this.currentVehicleStep = 'model';
  }

  selectModelStep(model: any) {
    this.selectedVehicle.model = model.name;
    this.selectedVehicle.modelId = model.id;
    
    // Update form
    this.registerForm.patchValue({ model: model.id });
    
    // Reset subsequent selections
    this.resetVehicleFieldsFrom('fuel');
    
    // Load fuel types for selected model
    this.loadFuelTypeList(model.id);
    
    // Move to next step
    this.currentVehicleStep = 'fuel';
  }

  selectFuelTypeStep(fuel: any) {
    this.selectedVehicle.fuel = fuel.name;
    this.selectedVehicle.fuelId = fuel.id;
    
    // Update form
    this.registerForm.patchValue({ fuelType: fuel.id });
    
    // Reset subsequent selections
    this.resetVehicleFieldsFrom('transmission');
    
    // Load transmission types
    this.loadTransmissionTypeList(fuel.id);
    
    // Move to next step
    this.currentVehicleStep = 'transmission';
  }

  selectTransmissionStep(transmission: any) {
    this.selectedVehicle.transmission = transmission.name;
    this.selectedVehicle.transmissionId = transmission.id;
    
    // Update form
    this.registerForm.patchValue({ transmissionType: transmission.id });
    
    // Reset subsequent selections
    this.resetVehicleFieldsFrom('year');
    
    // Load years
    this.loadRegYearList(transmission.id);
    
    // Move to next step
    this.currentVehicleStep = 'year';
  }

  selectYearStep(year: any) {
    this.selectedVehicle.year = year.year;
    this.selectedVehicle.yearId = year.id;
    
    // Update form
    this.registerForm.patchValue({ regDate: year.id });
    
    // Reset subsequent selections
    this.resetVehicleFieldsFrom('variant');
    
    // Load variants
    const transmissionId = this.registerForm.get('transmissionType')?.value;
    if (transmissionId && year.id) {
      this.loadVariantList(transmissionId, year.id);
    }
    
    // Move to next step
    this.currentVehicleStep = 'variant';
  }

  selectVariantStep(variant: any) {
    this.selectedVehicle.variant = variant.name;
    this.selectedVehicle.variantId = variant.id;
    
    // Update form
    this.registerForm.patchValue({ variant: variant.id });
    
    // Exit editing mode (if in edit mode)
    this.isEditingVehicle = false;
  }

  goBackStep() {
    const stepOrder = ['make', 'model', 'fuel', 'transmission', 'year', 'variant'];
    const currentIndex = stepOrder.indexOf(this.currentVehicleStep);
    
    if (currentIndex > 0) {
      this.currentVehicleStep = stepOrder[currentIndex - 1];
    }
  }

  editStep(step: string) {
    // When editing a step, reset all subsequent fields
    const stepOrder = ['make', 'model', 'fuel', 'transmission', 'year', 'variant'];
    const editIndex = stepOrder.indexOf(step);

    // Reset all fields after the step being edited
    if (editIndex < stepOrder.length - 1) {
      const nextStep = stepOrder[editIndex + 1];
      this.resetVehicleFieldsFrom(nextStep);
    }

    this.currentVehicleStep = step;
    this.isEditingVehicle = true;
  }

  editVehicleDetails() {
    this.isEditingVehicle = true;
  }

  resetVehicleFieldsFrom(fromStep: string) {
    const stepOrder = ['make', 'model', 'fuel', 'transmission', 'year', 'variant'];
    const fromIndex = stepOrder.indexOf(fromStep);
    
    for (let i = fromIndex; i < stepOrder.length; i++) {
      const step = stepOrder[i];
      switch (step) {
        case 'model':
          this.selectedVehicle.model = '';
          this.selectedVehicle.modelId = null;
          this.registerForm.patchValue({ model: '' });
          this.modelList = [];
          break;
        case 'fuel':
          this.selectedVehicle.fuel = '';
          this.selectedVehicle.fuelId = null;
          this.registerForm.patchValue({ fuelType: '' });
          this.fuelTypeList = [];
          break;
        case 'transmission':
          this.selectedVehicle.transmission = '';
          this.selectedVehicle.transmissionId = null;
          this.registerForm.patchValue({ transmissionType: '' });
          this.transmissionTypeList = [];
          break;
        case 'year':
          this.selectedVehicle.year = '';
          this.selectedVehicle.yearId = null;
          this.registerForm.patchValue({ regDate: '' });
          this.regYearList = [];
          break;
        case 'variant':
          this.selectedVehicle.variant = '';
          this.selectedVehicle.variantId = null;
          this.registerForm.patchValue({ variant: '' });
          this.variantList = [];
          break;
      }
    }
  }

  isStepCompleted(step: string): boolean {
    switch (step) {
      case 'make':
        return !!this.selectedVehicle.make;
      case 'model':
        return !!this.selectedVehicle.model;
      case 'fuel':
        return !!this.selectedVehicle.fuel;
      case 'transmission':
        return !!this.selectedVehicle.transmission;
      case 'year':
        return !!this.selectedVehicle.year;
      case 'variant':
        return !!this.selectedVehicle.variant;
      default:
        return false;
    }
  }

  isVehicleDetailsFullySelected(): boolean {
    if (!this.selectedVehicle) return false;
    const v = this.selectedVehicle;
    return !!(v.makeId && v.modelId && v.fuelId && v.transmissionId && v.yearId && v.variantId);
  }

  createPartFormGroup() {
    return this.formBuilder.group({
      partNo: [''],
      partDescription: [''],
      understoodPart: [''],
      qty: [],
      partAttachmentId: [null],
      partImg: [null],
      partDefinition: [''],
      isDescriptionSelected: [false],
      voiceDraftPartId: [null]
    });
  }

  createVehAttachmentFormGroup() {
    return this.formBuilder.group({
      attachmentType: [''],
      attachmentId: [''],
    });
  }

  // Location handling
  onLocationChange(event: any) {
    this.selectedLocation = event.target.value;
    this.registerForm.patchValue({
      location: this.selectedLocation,
    });
  }

  locationList() {
    this.http.get('retailers/getLocationsByAuthId').subscribe((res: any) => {
      this.searching = false;
      if (res.code === 1 && res.data.records.length === 1) {
        const [firstLocation] = res.data.records;
        this.locations = [firstLocation];
        this.selectedLocation = firstLocation.branchId;
        this.registerForm.patchValue({
          location: this.selectedLocation,
        });
      } else if (res.code === 1) {
        this.locations = res.data.records;
      } else {
        this.locations = [];
      }
    });
  }

  // Part management
  onPartNoInput(index: number, event: any) {
    const value = event.target.value;
    const part = this.partDetails.at(index);
    part.get('partDescription')?.enable();
    
    if (value && value.length >= 2) {
      this.activePartNoIndex = index;
      this.partNoSearchSubject.next({ query: value, index });
    } else {
      this.partNoSuggestions = [];
      this.activePartNoIndex = null;
    }
  }

  onPartNoFocus(index: number, event: any) {
    const value = event.target.value;
    if (value && value.length >= 2) {
      this.activePartNoIndex = index;
      if (this.partNoSuggestions.length === 0) {
        this.partNoSearchSubject.next({ query: value, index });
      }
    }
  }

  selectPartNo(item: any, index: number) {
    const part = this.partDetails.at(index);
    part.patchValue({
      partNo: item.item_code || '',
      partDescription: item.item_description || ''
    });
    // Disable description if selected from autocomplete
    part.get('partDescription')?.disable();
    
    this.partNoSuggestions = [];
    this.activePartNoIndex = null;
  }

  hidePartNoSuggestions() {
    setTimeout(() => {
      this.partNoSuggestions = [];
      this.activePartNoIndex = null;
    }, 200);
  }

  addPart() {
    this.partDetails.push(this.createPartFormGroup());
  }

  removePart(index: number) {
    this.partDetails.removeAt(index);
  }

  // Attachment handling
  onUploadTypeChange(event: any) {
    this.selectedVehicleAttachmentType = event.target.value;
  }

  newVehicleAttachment() {
    if (this.selectedVehicleAttachmentType) {
      const existingAttachment = this.vehicleAttachmentList.find(
        (attachment) =>
          attachment.attachmentType === this.selectedVehicleAttachmentType
      );
      if (existingAttachment) {
        this.notify.showError('Already Uploaded', '');
      } else {
        this.uploadImage();
      }
    }
  }

  async showVehicleAttachment(index: number, attachment: any) {
    window.open(attachment.url, '_blank');
  }

  openModalWithAttachments(attachments: any[]) {
    this.selectedAttachments = attachments;
    this.showAttachmentModal = true;
  }

    // Part details handling
  onPartNoType(event: any) {
    // Implement part number validation logic here
    console.log('Part number typed', event.target.value);
    if (event.target.value?.length > 1) {
      this.addPart();
    }
  }



  onQtyInput(index: number) {
    const partDetails = this.registerForm.get('partDetails') as FormArray;
    const qtyValue = partDetails.at(index).get('qty')?.value;
    if (qtyValue) {
      this.partErrors = this.partErrors.filter(
        err => !(err.index === index && err.field === 'qty')
      );
    }
  }



  uploadImage() {
    // Create a file input element
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = 'image/*'; // Accept only image files

    // Listen for changes (file selection)
    fileInput.onchange = async (event: Event) => {
      const target = event.target as HTMLInputElement;
      if (target.files && target.files.length > 0) {
        const file = target.files[0];

        // Check if the file type is an image
        if (!file.type.startsWith('image/')) {
          this.notify.showError('Please select a valid image file.', '');
          return;
        }

        // Create a FileReader to read the file
        const reader = new FileReader();
        reader.onload = async (e) => {
          const imgSrc = e.target?.result as string;

          // Create an image element to load the captured image
          const img = new Image();
          img.src = imgSrc;

          img.onload = async () => {
            const canvas = document.createElement('canvas');
            const maxWidth = 800;
            const maxHeight = 800;

            let { width, height } = img;

            // Resize the image if it exceeds the maximum dimensions
            if (width > height && width > maxWidth) {
              height = Math.round((height * maxWidth) / width);
              width = maxWidth;
            } else if (height > maxHeight) {
              width = Math.round((width * maxHeight) / height);
              height = maxHeight;
            }

            canvas.width = width;
            canvas.height = height;

            const ctx = canvas.getContext('2d');
            ctx?.drawImage(img, 0, 0, width, height);

            try {
              // Create a Blob from the canvas
              const blob = await this.createBlobFromCanvas(
                canvas,
                file.type,
                0.8
              );
              const formData = new FormData();
              formData.append('filename', blob, file.name);
              formData.append('type', this.selectedVehicleAttachmentType);
              formData.append('vehNo', this.registerForm.value.vehNo || '');

              // Show loading indicator
              this.loadingService.show();

              // Upload the image to the server
              const res: any = await firstValueFrom(
                this.http.filePost('retailers/enquiryImageUpload', formData)
              );

              if (res.code === 1) {
                const vehicleAttachment = {
                  attachmentType: this.selectedVehicleAttachmentType,
                  img: res.data.url,
                  attachmentId: res.data.attachmentId,
                };
                this.vehicleAttachmentList.push(vehicleAttachment);
                this.notify.showSuccess(res.message, '');
              } else {
                this.notify.showError(res.message, '');
              }
              this.loadingService.hide();
            } catch (blobError) {
              console.error('Blob creation error:', blobError);
              this.loadingService.hide();
              this.notify.showError('Error processing image.', '');
            }
          };
        };

        // Read the file as a Data URL
        reader.readAsDataURL(file);
      }
    };

    // Trigger the file input click
    fileInput.click();
  }

  createBlobFromCanvas(
    canvas: HTMLCanvasElement,
    type: string,
    quality: number
  ): Promise<Blob> {
    return new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error('Canvas is empty or conversion to Blob failed.'));
          }
        },
        type,
        quality
      );
    });
  }

  removeVehicleAttachment(index: number, attachmentId: string) {
    // Implement attachment removal logic here
    this.vehicleAttachmentList.splice(index, 1);
  }

  // Part details handling
  // onPartNoType(event: any) {
  //   // Implement part number validation logic here
  //   console.log('Part number typed', event.target.value);
  //   if (event.target.value?.length > 1) {
  //     this.addPart();
  //   }
  // }

  getPartError(index: number, field: string): string {
    const error = this.partErrors.find(
      (err) => err.index === index && err.field === field
    );
    return error ? error.message : '';
  }

  showPartAttachment(index: number): boolean {
    return true; // Implement your logic here
  }

  async addPartImage(index: number) {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async (event: Event) => {
      const file = (event.target as HTMLInputElement).files?.[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = () => {
        const img = new Image();
        img.src = reader.result as string;

        img.onload = async () => {
          const canvas = document.createElement('canvas');
          const maxWidth = 800;
          const maxHeight = 800;

          let { width, height } = img;

          if (width > height && width > maxWidth) {
            height = Math.round((height * maxWidth) / width);
            width = maxWidth;
          } else if (height > maxHeight) {
            width = Math.round((width * maxHeight) / height);
            height = maxHeight;
          }

          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext('2d');
          ctx?.drawImage(img, 0, 0, width, height);

          try {
            const blob = await new Promise<Blob>((resolve, reject) => {
              canvas.toBlob(
                (b) => (b ? resolve(b) : reject('Failed to create blob')),
                file.type,
                0.8
              );
            });

            const formData = new FormData();
            formData.append('filename', blob, 'image.png');
            formData.append('vehNo', this.registerForm.value.vehNo || '');

            // Show loading indicator
            this.loadingService.show();

            const res: any = await firstValueFrom(
              this.http.filePost('retailers/enquiryPartImageUpload', formData)
            );

            if (res.code === 1) {
              const partDetails = this.registerForm.get(
                'partDetails'
              ) as FormArray;
              const partDetail = partDetails.at(index);
              if (partDetail) {
                partDetail.patchValue({
                  partAttachmentId: res.data.partAttachmentId,
                  partImg: res.data.url,
                });
              }
              this.notify.showSuccess(res.message, '');
            } else {
              this.notify.showError(res.message, '');
            }
            this.loadingService.hide();
          } catch (error) {
            console.error('Image upload error:', error);
            this.loadingService.hide();
            this.notify.showError('Error processing image.', '');
          }
        };
      };
      reader.readAsDataURL(file);
    };

    input.click();
  }

  async showPartDetailsAttachment(index: number, attachment: any) {
    window.open(attachment.value.partImg, '_blank');
  }

  // Form submission
  onSubmit() {
    this.isLoading = true;

    if (!this.selectedLocation) {
      this.notify.showError('Kindly Select the location', '');
      this.isLoading = false;
      return;
    }

    // Validate make and model
    if (!this.registerForm.get('make')?.value) {
      this.notify.showError('Please select a make', '');
      this.isLoading = false;
      return;
    }

    if (!this.registerForm.get('model')?.value) {
      this.notify.showError('Please select a model', '');
      this.isLoading = false;
      return;
    }

    // Update location and vehNo
    const vehNoValue = this.registerForm.value.vehNo;
    this.registerForm.patchValue({
      location: this.selectedLocation,
      vehNo: vehNoValue ? vehNoValue.toUpperCase() : '',
    });

    if (this.registerForm.invalid) {
      this.registerForm.markAllAsTouched();
      this.isLoading = false;
      return;
    }

    // Validate parts
    // this.validateParts();
    // if (this.partErrors.length > 0) {
    //   this.isLoading = false;
    //   return;
    // }

        // Clean up partDetails array
    const partDetailsArray = this.registerForm.get('partDetails') as FormArray;
    this.partErrors = [];

    for (let i = partDetailsArray.length - 1; i >= 0; i--) {
      const part = partDetailsArray.at(i).value;
      const isEmpty = !part.partDescription && !part.qty && !part.partNo;

      if (isEmpty && i !== 0) {
        partDetailsArray.removeAt(i);
      } else {
        if (!part.partDescription) {
          this.partErrors.push({
            index: i,
            field: 'partDescription',
            message: 'Required',
          });
        } 
        // else if (part.partDescription && !part.isDescriptionSelected) {
        //   this.partErrors.push({
        //     index: i,
        //     field: 'partDescription',
        //     message: 'Please select a part from the suggestion list',
        //   });
        // }
        if (!part.qty) {
          this.partErrors.push({ index: i, field: 'qty', message: 'Required' });
        }
      }
    }

    // If part detail errors exist
    if (this.partErrors.length > 0) {
      this.notify.showError('Please fill all required part details.', '');
      this.isLoading = false;
      return;
    }

    // Prepare vehicle attachments
    const formValue = this.registerForm.getRawValue();

    if (formValue.partDetails && formValue.partDetails.length > 0) {
      formValue.partDetails.forEach((part: any) => {
        part.isUserGivenPart = part.partNo && part.partNo.trim() !== '' ? 1 : 0;
      });
    }

    // Convert boolean to numeric 0 or 1
    // formValue.isBulkupload = formValue.isBulkupload ? 1 : 0;
    formValue.isBulkupload =  0;

    if (this.voiceDraftId) {
      formValue.voiceDraftId = this.voiceDraftId;
      
      const vehicleChanges: any = {};
      const mapKeyToForm = [
        { origKey: 'make', formKey: 'make', list: this.makeList, nameProp: 'title' },
        { origKey: 'model', formKey: 'model', list: this.modelList, nameProp: 'name' },
        { origKey: 'fuelType', formKey: 'fuelType', list: this.fuelTypeList, nameProp: 'name' },
        { origKey: 'transmissionType', formKey: 'transmissionType', list: this.transmissionTypeList, nameProp: 'name' },
        { origKey: 'regDate', formKey: 'regDate', list: this.regYearList, nameProp: 'year' },
        { origKey: 'variant', formKey: 'variant', list: this.variantList, nameProp: 'name' }
      ];

      for (const mapping of mapKeyToForm) {
        const origData = this.originalVoiceVehicle?.[mapping.origKey];
        const currentId = formValue[mapping.formKey];
        
        if (origData && origData.id && String(origData.id) !== String(currentId)) {
          const selectedItem = mapping.list.find((item: any) => String(item.id) === String(currentId));
          vehicleChanges[mapping.formKey] = {
            from: { id: origData.id, name: origData.name },
            to: { id: currentId, name: selectedItem ? selectedItem[mapping.nameProp] : '' }
          };
        }
      }
      
      if (Object.keys(vehicleChanges).length > 0) {
        formValue.vehicleChanges = vehicleChanges;
      }
    }

    if (this.vehicleAttachmentList?.length > 0) {
      formValue.vehicleAttachmentDetails = this.vehicleAttachmentList.map(
        (attachment) => ({
          attachmentId: attachment.attachmentId,
          attachmentType: attachment.attachmentType,
        })
      );
    }

    if (this.noteId) {
      formValue.enquiryNotesId = this.noteId;
    }

    // Submit form
    this.http.post('retailers/saveEnquiry', formValue).subscribe(
      (res: any) => {
        this.isLoading = false;
        if (res.code === 1) {
          // Success - navigate back
          this.notify.showSuccess(res.message, '');
          this.router.navigate(['/dashboard']);
          this.enquireService.triggerRefreshList();
        } else {
          // Show error message
          this.notify.showError(res.message, '');
        }
      },
      (error) => {
        this.isLoading = false;
        this.notify.showError('An error occurred. Please try again.', '');
      }
    );
  }

  private validateParts() {
    this.partErrors = [];
    const partDetails = this.partDetails;

    for (let i = partDetails.length - 1; i >= 0; i--) {
      const part = partDetails.at(i).value;
      const allFieldsEmpty = !part.partDescription && !part.qty && !part.partNo;

      if (allFieldsEmpty && i !== 0) {
        partDetails.removeAt(i);
      } else {
        if (!part.partDescription) {
          this.partErrors.push({
            index: i,
            field: 'partDescription',
            message: 'Required',
          });
        }
        if (!part.qty) {
          this.partErrors.push({
            index: i,
            field: 'qty',
            message: 'Required',
          });
        }
      }
    }
  }

  // Utility methods
  getAttachmentValue(attachmentTypeId: number): string {
    const attachment = this.vehicleDocList.find(
      (item) => item.id == attachmentTypeId
    );
    console.log('attachment', attachment);
    return attachment ? attachment.value : 'Unknown';
  }

  trackByFn(index: number): any {
    return index;
  }

  closeModal() {
    this.showAttachmentModal = false;
  }

  get isAnyPartFilled(): boolean {
    return this.partDetails.controls.some((control) => {
      const desc = control.get('partDescription')?.value?.trim();
      const qty = control.get('qty')?.value;
      return desc && qty > 0;
    });
  }

  maskValue(value: string): string {
    if (!value) return '-';
    return value.length > 2 ? '********' + value.slice(-2) : '**';
  }

  downloadTemplate() {
    const headers = ['Part No *', 'Description', 'Qty *'];
    const data = [headers];

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(data);

    const headerStyle = {
      fill: { patternType: 'solid', fgColor: { rgb: '00E5E5' } },
      font: { bold: true, color: { rgb: '000000' } },
      alignment: { horizontal: 'center', vertical: 'center' },
    } as any;

    ['A1', 'B1', 'C1'].forEach((cell) => {
      if (ws[cell]) {
        ws[cell].s = headerStyle;
      }
    });

    ws['!cols'] = [{ wch: 18 }, { wch: 40 }, { wch: 10 }];
    XLSX.utils.book_append_sheet(wb, ws, 'Template');

    const wbout = XLSX.write(wb, {
      bookType: 'xlsx',
      type: 'array',
    });

    const blob = new Blob([wbout], {
      type: 'application/octet-stream',
    });
    saveAs(blob, 'part_template.xlsx');

    this.templateDownloaded = true;
  }

  handleFileChange(event: any): void {
    const file = event.target.files[0];
    console.log('file', file);
    if (file) {
      this.selectedExcelFile = file;
    }
  }

  onExcelUpload(): void {
    if (!this.selectedExcelFile) {
      alert('Please select a file first.');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e: any) => {
      const data = e.target.result;
      const workbook = XLSX.read(data, { type: 'binary' });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const rawJson = XLSX.utils.sheet_to_json(sheet, {
        defval: '',
        blankrows: false,
      });

      this.excelPreview = rawJson
        .map((row: any) => ({
          partNo: row['Part No *'] || row['Part No'] || '',
          partDescription: (row['Description'] || '').toString().trim(),
          qty:
            row['Qty *'] !== undefined
              ? row['Qty *']
              : row['Qty'] !== undefined
                ? row['Qty']
                : '',
        }))
        .filter(
          (row) => row.partDescription || row.qty.toString().trim() !== ''
        );

      const hasValid = this.excelPreview.some(
        (r) =>
          (r.partDescription.length > 0 || r.partNo.length > 0) &&
          r.qty !== '' &&
          !isNaN(Number(r.qty))
      );

      if (!hasValid) {
        alert(
          'Please make sure at least one row has a valid Part No or Description and Qty.'
        );
        return;
      }

      this.excelDataLoaded = true;
      this.openExcelModal();
    };
    reader.readAsBinaryString(this.selectedExcelFile);
  }

  openExcelModal() {
    if (this.excelPreview.length > 0) {
      this.modalService.open(this.excelPreviewModal, {
        size: 'lg',
        backdrop: 'static',
      });
    }
  }

  confirmExcelData(form: NgForm, modalRef: NgbModalRef) {
    form.control.markAllAsTouched(); // force validation

    const hasError = this.excelPreview.some(
      (row) =>
        !row.partDescription?.trim() ||
        row.qty === null ||
        row.qty === '' ||
        isNaN(Number(row.qty))
    );

    if (hasError) {
      return;
    }

    const partDetailsArray = this.registerForm.get('partDetails') as FormArray;
    partDetailsArray.clear();

    for (const row of this.excelPreview) {
      const group = this.formBuilder.group({
        partNo: [row.partNo],
        partDescription: [row.partDescription, Validators.required],
        qty: [row.qty, Validators.required],
        partAttachmentId: [null],
        partImg: [null],
      });
      partDetailsArray.push(group);
    }

    this.registerForm.patchValue({ isBulkupload: true });

    this.excelDataFetched = true;

    modalRef.close();
  }

  isInvalidQty(value: any): boolean {
    return value === null || value === '' || isNaN(Number(value));
  }

  async clearSelectedExcelFile() {
    const partDetailsArray = this.registerForm.get('partDetails') as FormArray;

    // Only prompt if there's data already fetched from Excel
    if (this.excelDataFetched && partDetailsArray?.length) {
      const modalRef = this.modalService.open(ConfirmDialogComponent, {
        centered: true,
      });
      modalRef.componentInstance.title = 'Clear Uploaded Data';
      modalRef.componentInstance.message =
        'You already have part entries loaded. Do you really want to clear the file and all related part data?';
      modalRef.componentInstance.btnCancelText = 'Cancel';
      modalRef.componentInstance.btnOkText = 'Clear';

      try {
        await modalRef.result; // only proceeds if user clicks "Clear"
      } catch {
        return; // user cancelled, stop without clearing
      }
    }

    // Clear the selected file and previewed data
    this.selectedExcelFile = null;
    this.excelPreview = [];
    this.excelDataLoaded = false;
    this.excelDataFetched = false;

    // Clear all partDetail rows
    if (partDetailsArray) {
      partDetailsArray.clear();
      partDetailsArray.push(this.createPartFormGroup());
    }

    this.notify.showSuccess('Uploaded file and part data cleared.', '');
  }

  removeExcelPreviewRow(index: number) {
    this.excelPreview.splice(index, 1);
  }

  allowOnlyNumbers(event: KeyboardEvent) {
    const charCode = event.key.charCodeAt(0);
    if (charCode < 48 || charCode > 57) {
      event.preventDefault();
    }
  }

  // --- VOICE LOGIC ---
  isRecording = false;
  isPlayingExample = false;
  isPlayingRecording = false;
  recordedAudioBlob: Blob | null = null;
  recordedAudioUrl: string | null = null;
  private mediaRecorder: MediaRecorder | null = null;
  private audioChunks: Blob[] = [];
  private audioObj = new Audio();
  private exampleAudio: HTMLAudioElement | null = null;
  private recordedAudio: HTMLAudioElement | null = null;
  detectedVehicle: any = {};

  playExampleVoice(event: Event) {
    event.stopPropagation();
    const audioPath = '/assets/audio/voice-example.mp3';

    if (this.isPlayingExample && this.exampleAudio) {
      this.exampleAudio.pause();
      this.exampleAudio.currentTime = 0;
      this.isPlayingExample = false;
      return;
    }

    if (this.exampleAudio) {
      this.exampleAudio.pause();
      this.exampleAudio.currentTime = 0;
      this.exampleAudio = null;
    }

    this.exampleAudio = new Audio(audioPath);
    this.exampleAudio.addEventListener('ended', () => {
      this.isPlayingExample = false;
      this.exampleAudio = null;
    });
    this.exampleAudio.addEventListener('error', (e) => {
      console.error('Error playing audio:', e);
      this.isPlayingExample = false;
      this.exampleAudio = null;
      this.notify.showError('Could not play example audio', '');
    });

    this.isPlayingExample = true;
    this.exampleAudio.play().catch(err => {
      console.error('Play error:', err);
      this.isPlayingExample = false;
      this.exampleAudio = null;
      this.notify.showError('Could not play example audio', '');
    });
  }

  async startVehicleRecording() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      this.mediaRecorder = new MediaRecorder(stream);
      this.audioChunks = [];
      
      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          this.audioChunks.push(event.data);
        }
      };

      this.mediaRecorder.onstop = () => {
        this.recordedAudioBlob = new Blob(this.audioChunks, { type: 'audio/webm' });
        this.recordedAudioUrl = URL.createObjectURL(this.recordedAudioBlob);
        this.audioChunks = [];
        this.isRecording = false;
      };

      this.mediaRecorder.start();
      this.isRecording = true;
    } catch (err) {
      console.error('Microphone access error:', err);
      this.notify.showError('Microphone access denied or not supported', '');
    }
  }

  stopVehicleRecording() {
    if (this.mediaRecorder && this.isRecording) {
      this.mediaRecorder.stop();
      this.mediaRecorder.stream.getTracks().forEach(track => track.stop());
    }
  }

  playRecordedAudio() {
    if (!this.recordedAudioBlob) return;

    if (this.isPlayingRecording && this.recordedAudio) {
      this.recordedAudio.pause();
      this.recordedAudio.currentTime = 0;
      this.isPlayingRecording = false;
      return;
    }

    if (!this.recordedAudioUrl) {
      this.recordedAudioUrl = URL.createObjectURL(this.recordedAudioBlob);
    }

    if (!this.recordedAudio) {
      this.recordedAudio = new Audio(this.recordedAudioUrl);

      this.recordedAudio.addEventListener('ended', () => {
        this.isPlayingRecording = false;
      });

      this.recordedAudio.addEventListener('error', (e) => {
        console.error('Error playing recorded audio:', e);
        this.isPlayingRecording = false;
        this.notify.showError('Could not play recording', '');
      });
    }

    this.isPlayingRecording = true;
    this.recordedAudio.play().catch(err => {
      console.error('Play error:', err);
      this.isPlayingRecording = false;
      this.notify.showError('Could not play recording', '');
    });
  }

  deleteRecording() {
    this.recordedAudioBlob = null;
    if (this.recordedAudioUrl) {
      URL.revokeObjectURL(this.recordedAudioUrl);
      this.recordedAudioUrl = null;
    }
    if (this.recordedAudio) {
      this.recordedAudio.pause();
      this.recordedAudio = null;
    }
    this.isPlayingRecording = false;
  }

  sendRecordedAudio() {
    if (!this.recordedAudioBlob) {
      this.notify.showError('No recording to send', '');
      return;
    }
    this.processAudio(this.recordedAudioBlob);
  }

  onExistingAudioSelected(event: any) {
    const target = event.target as HTMLInputElement;
    const file = target.files && target.files.length > 0 ? target.files[0] : null;

    if (!file) {
      return;
    }

    const fileName = (file.name || '').toLowerCase();
    
    // We will warn but not block if the extension isn't in our known list,
    // to prevent blocking valid files that the OS miscategorizes.
    const supportedAudioExtensions = [
      '.webm', '.mp3', '.wav', '.m4a', '.aac', '.ogg', '.oga', '.opus', '.amr', '.3gp', '.mp4'
    ];
    
    const hasAudioMime = !!file.type && (file.type.startsWith('audio/') || file.type.startsWith('video/'));
    const hasSupportedExtension = supportedAudioExtensions.some(ext => fileName.endsWith(ext));

    if (!hasAudioMime && !hasSupportedExtension) {
      this.notify.showError(`Warning: Unknown audio type (${file.type || 'none'}). Attempting to use anyway.`, '');
    }

    if (this.recordedAudio) {
      this.recordedAudio.pause();
      this.recordedAudio = null;
    }
    this.isPlayingRecording = false;

    if (this.recordedAudioUrl) {
      URL.revokeObjectURL(this.recordedAudioUrl);
      this.recordedAudioUrl = null;
    }

    this.recordedAudioBlob = file;
    this.notify.showSuccess(`Audio file selected: ${file.name}`, '');
    target.value = '';
  }

  private setVoiceEnquiryType() {
    this.registerForm?.get('enquiryType')?.setValue('5');
  }

  async processAudio(audioBlob: Blob) {
    this.isLoading = true;
    try {
      const formData = new FormData();
      if (audioBlob && typeof audioBlob === 'object' && (audioBlob as any).name) {
        const audioFile: any = audioBlob;
        const fileName = audioFile.name || 'voice.m4a';
        formData.append('audio', audioFile, fileName);
      } else {
        const isM4a = audioBlob.type === 'audio/aac' || audioBlob.type === 'audio/mp4';
        const ext = isM4a ? 'voice.m4a' : 'voice.webm';
        formData.append('audio', audioBlob, ext);
      }

      const res: any = await firstValueFrom(
        this.http.filePost('retailers/getVehicleDetailsByVoiceV3', formData)
      );

      console.log('API Response:', res);

      if (res && res.code === 1 && res.data) {
        this.voiceDraftId = res.data.voice_draft_id || null;
        
        const v = res.data.vehicle_details || {};
        const order = [
          { key: 'make', id: 'make', name: 'makename' },
          { key: 'model', id: 'model', name: 'modelname' },
          { key: 'fuel', id: 'fuel', name: 'fuelname' },
          { key: 'transmission', id: 'transmission', name: 'transmissionname' },
          { key: 'year', id: 'year', name: 'yearname' },
          { key: 'variant', id: 'variant', name: 'variantname' }
        ];
        let detectedVehicle: any = {};
        let selectedVehicle: any = {};
        let firstMissing = null;
        for (const step of order) {
          const idVal = v[step.id];
          const nameVal = v[step.name];
          if (idVal && nameVal) {
            detectedVehicle[step.key + 'Id'] = idVal;
            detectedVehicle[step.key] = nameVal;
            selectedVehicle[step.key + 'Id'] = idVal;
            selectedVehicle[step.key] = nameVal;
          } else if(nameVal){
            detectedVehicle[step.key] = nameVal;
            selectedVehicle[step.key] = nameVal;
          }else {
            if (!firstMissing) firstMissing = step.key;
          }
        }
        this.detectedVehicle = detectedVehicle;
        this.selectedVehicle = {
          make: detectedVehicle.make || '',
          makeId: detectedVehicle.makeId || null,
          model: detectedVehicle.model || '',
          modelId: detectedVehicle.modelId || null,
          fuel: detectedVehicle.fuel || '',
          fuelId: detectedVehicle.fuelId || null,
          transmission: detectedVehicle.transmission || '',
          transmissionId: detectedVehicle.transmissionId || null,
          year: detectedVehicle.year || '',
          yearId: detectedVehicle.yearId || null,
          variant: detectedVehicle.variant || '',
          variantId: detectedVehicle.variantId || null
        };

        this.originalVoiceVehicle = {
          make: { id: detectedVehicle.makeId, name: detectedVehicle.make },
          model: { id: detectedVehicle.modelId, name: detectedVehicle.model },
          fuelType: { id: detectedVehicle.fuelId, name: detectedVehicle.fuel },
          transmissionType: { id: detectedVehicle.transmissionId, name: detectedVehicle.transmission },
          regDate: { id: detectedVehicle.yearId, name: detectedVehicle.year },
          variant: { id: detectedVehicle.variantId, name: detectedVehicle.variant }
        };

        this.registerForm.patchValue({
          make: this.selectedVehicle.makeId,
          model: this.selectedVehicle.modelId,
          fuelType: this.selectedVehicle.fuelId,
          transmissionType: this.selectedVehicle.transmissionId,
          regDate: this.selectedVehicle.yearId,
          variant: this.selectedVehicle.variantId
        });

        const hasDetectedVehicleData = Object.keys(detectedVehicle).length > 0;
        const hasDetectedVehicleIds = !!(
          detectedVehicle.makeId ||
          detectedVehicle.modelId ||
          detectedVehicle.fuelId ||
          detectedVehicle.transmissionId ||
          detectedVehicle.yearId ||
          detectedVehicle.variantId
        );

        this.currentVehicleStep = firstMissing || 'variant';

        if (hasDetectedVehicleIds) {
          this.loadMakeList();
        } 

        if (detectedVehicle.makeId) {
          this.loadModelList(detectedVehicle.makeId);
        }
        if (detectedVehicle.modelId) {
          this.loadFuelTypeList(detectedVehicle.modelId);
        }
        if (detectedVehicle.fuelId) {
          this.loadTransmissionTypeList(detectedVehicle.fuelId);
        }
        if (detectedVehicle.transmissionId) {
          this.loadRegYearList(detectedVehicle.transmissionId);
        }
        if (detectedVehicle.transmissionId && detectedVehicle.yearId) {
          this.loadVariantList(detectedVehicle.transmissionId, detectedVehicle.yearId);
        }

        const partDetailsArray = this.registerForm.get('partDetails') as import('@angular/forms').FormArray;
        const parts = res.data.parts || [];
        const validParts = parts.filter((p: any) => !!p?.part_name?.toString().trim());
        const hasPartData = validParts.length > 0;

        if (!hasDetectedVehicleIds && !hasPartData) {
          this.isLoading = false;
          this.notify.showError(
            hasDetectedVehicleData
              ? 'Voice detected vehicle text, but details could not be mapped'
              : 'Voice processed but no vehicle or part details were detected',
            ''
          );
          return;
        }

        if (hasPartData) {
          while (partDetailsArray.length > 1) {
            partDetailsArray.removeAt(0);
          }

          validParts.forEach((p: any, index: number) => {
            if (index === 0 && partDetailsArray.length === 1) {
              partDetailsArray.at(0).patchValue({
                partDescription: p.part_name,
                qty: p.qty || 1,
                partDefinition: p.part_definition || '',
                understoodPart: p.understood_part || '',
                isDescriptionSelected: true,
                voiceDraftPartId: p.voice_draft_part_id || null
              });
            } else {
              const newPartGroup = this.createPartFormGroup();
              newPartGroup.patchValue({
                partDescription: p.part_name,
                qty: p.qty || 1,
                partDefinition: p.part_definition || '',
                understoodPart: p.understood_part || '',
                isDescriptionSelected: true,
                voiceDraftPartId: p.voice_draft_part_id || null
              });
              partDetailsArray.push(newPartGroup);
            }
          });

          // Add an extra empty row after populated rows
          this.addPart();
        }

        this.setVoiceEnquiryType();
        this.notify.showSuccess('Voice details updated!', '');
      } else {
        console.warn('Invalid response or no data', res);
        this.notify.showError('No matching vehicle details found', '');
      }
    } catch (error) {
      console.error('Process Audio Error:', error);
      this.notify.showError('Failed to process voice', '');
    } finally {
      this.isLoading = false;
    }
  }
}